import React, { FC, KeyboardEvent, useEffect, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Input, cn } from '@column/ui';

import { useDebounce, useInstitution } from '~/hooks';
import { FinancialInstitution } from '~/repositories';
import { validateBIC, validateRoutingNumber } from '~/util';

/** Looks up the financial institution for a routing number or BIC as it's typed. */
const useRoutingNumberInstitution = () => {
  const [institution, setInstitution] = useState<FinancialInstitution | null>(null);
  const { createRequest: fetchInstitution } = useInstitution({
    onSuccess: setInstitution,
    onError: () => setInstitution(null),
  });

  const [checkRoutingNumber] = useDebounce((rtnNum: string) => {
    if (validateRoutingNumber(rtnNum) || validateBIC(rtnNum)) {
      fetchInstitution({ routingNumber: rtnNum });
      return;
    }
    setInstitution(null);
  }, 200);

  return { institution, checkRoutingNumber };
};

interface RoutingNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onInstitutionChange?: (institution: FinancialInstitution | null) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
}

/** Routing number input that looks up the bank as the value changes (typed or set programmatically) and shows its name. */
export const RoutingNumberInput: FC<RoutingNumberInputProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  onInstitutionChange,
  placeholder = 'Routing number',
  disabled,
  invalid,
}) => {
  const { institution, checkRoutingNumber } = useRoutingNumberInstitution();
  const institutionName = institution?.fullName ?? institution?.shortName;

  useEffect(() => {
    checkRoutingNumber(value);
  }, [value, checkRoutingNumber]);

  useEffect(() => {
    onInstitutionChange?.(institution);
  }, [institution, onInstitutionChange]);

  return (
    <>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid}
      />
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200',
          institution ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <p className="mt-1.5 flex items-center gap-1 truncate text-xs leading-5 font-medium text-seafoam-600">
            <Icon.CircleCheck className="shrink-0 [--icon-size-x:16px] [--icon-size-y:16px]" />
            {institutionName}
          </p>
        </div>
      </div>
    </>
  );
};
